/* eslint-disable */
import React from "react";
import { Button, Tag } from "antd";
import {
    PiRobotBold,
    PiCaretDownBold,
    PiCaretUpBold,
    PiChatCircleDotsBold,
    PiMapPinBold,
    PiBriefcaseBold,
    PiClockBold,
} from "react-icons/pi";
import styled from "styled-components";
import { useLocalStorage } from "../hooks";

const SummaryCardContainer = styled.div<{ isCollapsed?: boolean }>`
    margin-bottom: 24px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #dbeafe 100%);
    border-radius: 16px;
    border: 2px solid #00a9b2;
    box-shadow: 0 4px 16px rgba(0, 169, 178, 0.15);
    overflow: hidden;
    transition: all 0.3s ease;
    animation: slideDown 0.5s ease forwards;
    position: sticky;
    top: 0;
    z-index: 999999;
    
    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const SummaryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    flex-wrap: wrap;

    &:hover {
        background-color: rgba(255, 255, 255, 0.3);
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    flex-wrap: wrap;
`;

const IconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #00a9b2, #0075b2);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 169, 178, 0.3);
    animation: pulse 2s infinite;

    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.05);
        }
    }
`;

const HeaderContent = styled.div`
    flex: 1;
    display: flex;
    flex-wrap: wrap;
`;

const HeaderTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 2px;
`;

const HeaderSubtitle = styled.div`
    font-size: 13px;
    color: #0369a1;
    opacity: 0.8;
`;

const CollapseButton = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.5);
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.8);
        transform: scale(1.1);
    }
`;

const SummaryContent = styled.div<{ isCollapsed?: boolean }>`
    max-height: ${({ isCollapsed }) => (isCollapsed ? '0' : '1000px')};
    opacity: ${({ isCollapsed }) => (isCollapsed ? '0' : '1')};
    overflow: hidden;
    transition: all 0.4s ease;
    padding: ${({ isCollapsed }) => (isCollapsed ? '0 24px' : '0 24px 0px 24px')};
    flex-wrap: wrap;
`;

const ActionsRow = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-right: 16px;
`;

const ActionButton = styled(Button)`
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const ContextInfo = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
    padding: 16px;
    background-color: rgba(255, 255, 255, 0.6);
    border-radius: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #0c4a6e;

    svg {
        color: #00a9b2;
    }
`;

export interface AiJobsSummaryCardProps {
    sessionId: string | null;
    messages: any[];
    jobsCount: number;
    onViewChat?: () => void;
    isLoadingMessages?: boolean;
    sessionContext?: any; // Session context_data
    onNewChat?: () => void; // Add new chat handler
}

export const AiJobsSummaryCard: React.FC<AiJobsSummaryCardProps> = ({
    sessionId,
    messages,
    jobsCount,
    onViewChat,
    isLoadingMessages = false,
    sessionContext,
    onNewChat
}) => {
    const [isCollapsed, setIsCollapsed] = useLocalStorage(`ai-jobs-summary-collapsed-${sessionId}`, false);

    // Extract collected data from session context
    const collectedData = sessionContext?.collected || {};

    // Show loading placeholder while messages are being fetched
    if (isLoadingMessages) {
        return (
            <SummaryCardContainer>
                <SummaryHeader style={{ cursor: 'default' }}>
                    <HeaderLeft>
                        <IconWrapper>
                            <PiRobotBold size={24} color="white" />
                        </IconWrapper>
                        <HeaderContent>
                            <HeaderTitle>Ihre personalisierten Ergebnisse</HeaderTitle>
                            <HeaderSubtitle>Lade Zusammenfassung...</HeaderSubtitle>
                        </HeaderContent>
                    </HeaderLeft>
                </SummaryHeader>
            </SummaryCardContainer>
        );
    }

    // Ensure messages is an array before using .find()
    const messagesArray = Array.isArray(messages) ? messages : [];

    // Extract summary message from chat history
    // Looking for message that starts with "Okay, lassen Sie uns kurz durchgehen"
    const summaryMessage = messagesArray.find((msg: any) => {
        const text = msg.text || msg.message || '';
        return text.includes('lassen Sie uns kurz durchgehen') ||
               text.includes('Okay, lassen Sie uns kurz durchgehen');
    });

    // If no summary message found AND no jobs, show "no jobs found" message
    if (!summaryMessage && jobsCount === 0) {
        return (
            <SummaryCardContainer>
                <SummaryHeader style={{ cursor: 'default' }}>
                    <HeaderLeft>
                        <IconWrapper>
                            <PiRobotBold size={24} color="white" />
                        </IconWrapper>
                        <HeaderContent>
                            <HeaderTitle>Keine Jobs gefunden</HeaderTitle>
                            <HeaderSubtitle>
                                Leider wurden keine passenden Jobs für Ihre Suche gefunden. Versuchen Sie es mit anderen Suchkriterien.
                            </HeaderSubtitle>
                        </HeaderContent>
                    </HeaderLeft>
                    {onNewChat && (
                        <ActionsRow>
                            <ActionButton
                                type="primary"
                                icon={<PiChatCircleDotsBold size={16} />}
                                onClick={onNewChat}
                            >
                                Neue Suche starten
                            </ActionButton>
                        </ActionsRow>
                    )}
                </SummaryHeader>
            </SummaryCardContainer>
        );
    }

    // If no summary message found, don't render the card
    if (!summaryMessage) {
        return null;
    }

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <SummaryCardContainer isCollapsed={isCollapsed}>
            <SummaryHeader onClick={toggleCollapse}>
                <HeaderLeft>
                    <IconWrapper>
                        <PiRobotBold size={24} color="white" />
                    </IconWrapper>
                    <HeaderContent>
                        <div>
                        <HeaderTitle>Ihre personalisierten Ergebnisse</HeaderTitle>
                        <HeaderSubtitle>
                            Basierend auf Ihrer Unterhaltung · {jobsCount} {jobsCount === 1 ? 'Job' : 'Jobs'} gefunden
                        </HeaderSubtitle>
                        </div>
                        <div style={{ display: "flex", gap: 12, marginLeft: 12, flexWrap: "wrap" }}>
                        {collectedData.job_position && (
                            <InfoItem>
                                <PiBriefcaseBold size={16} />
                                <Tag color="blue">{collectedData.job_position}</Tag>
                            </InfoItem>
                        )}
                        {collectedData.location && (
                            <InfoItem>
                                <PiMapPinBold size={16} />
                                <span>{collectedData.location}</span>
                                {collectedData.postal_code && <span> ({collectedData.postal_code})</span>}
                                {collectedData.radius && <Tag color="cyan">{collectedData.radius}</Tag>}
                            </InfoItem>
                        )}
                        {collectedData.hours_per_week && (
                            <InfoItem>
                                <PiClockBold size={16} />
                                <Tag color="green">{collectedData.hours_per_week} Std./Woche</Tag>
                            </InfoItem>
                        )}
                        </div>
                    </HeaderContent>
                </HeaderLeft>
                <ActionsRow>
                    {onViewChat && (
                        <ActionButton
                            icon={<PiChatCircleDotsBold size={16} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewChat();
                            }}
                        >
                            Chat anzeigen
                        </ActionButton>
                    )}
                </ActionsRow>
                <CollapseButton>
                    {isCollapsed ? (
                        <PiCaretDownBold size={18} color="#0369a1" />
                    ) : (
                        <PiCaretUpBold size={18} color="#0369a1" />
                    )}
                </CollapseButton>
            </SummaryHeader>

            <SummaryContent isCollapsed={isCollapsed}>
                {Object.keys(collectedData).length > 0 && (
                    <ContextInfo>
                        {collectedData.roles_interested && Array.isArray(collectedData.roles_interested) && collectedData.roles_interested.length > 0 && (
                            <InfoItem style={{ flexBasis: '100%', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ fontWeight: 600 }}>Interessierte Rollen:</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {collectedData.roles_interested.map((role: string, idx: number) => (
                                        <Tag key={idx} color="purple">{role}</Tag>
                                    ))}
                                </div>
                            </InfoItem>
                        )}
                    </ContextInfo>
                )}
            </SummaryContent>
        </SummaryCardContainer>
    );
};
